import { Fragment, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";

import clsx from "clsx";
import objectPath from "object-path";

import KTMenu from "_metronic/_assets/js/menu";
import KTUtil from "_metronic/_assets/js/util";
import * as builder from "_metronic/ducks/builder";
import MenuList from "_metronic/layout/aside/MenuList";
import { removeCSSClass } from "_metronic/utils/utils";

import { setPage } from "app/store/modules/actions/requests.actions";

const Menu = () => {
  const asideMenuRef = useRef();
  const dispatch = useDispatch();
  const location = useLocation();

  // Redux selectors using useSelector hook
  const menuConfig = useSelector((store) => store.builder.menuConfig);
  const layoutConfig = useSelector((store) => store.builder.layoutConfig);
  const headerLogo = useSelector((store) => builder.selectors.getLogo(store));
  const asideMenuAttr = useSelector((store) =>
    builder.selectors.getAttributes(store, { path: "aside_menu" })
  );
  const disableAsideSelfDisplay = useSelector(
    (store) => builder.selectors.getConfig(store, "aside.self.display") === false
  );
  const ulClasses = useSelector((store) =>
    builder.selectors.getClasses(store, {
      path: "aside_menu_nav",
      toString: true,
    })
  );
  const asideClassesFromConfig = useSelector((store) =>
    builder.selectors.getClasses(store, {
      path: "aside_menu",
      toString: true,
    })
  );

  let insideTm = useRef(null);
  let outsideTm = useRef(null);

  const initMenu = useCallback(() => {
    if (
      objectPath.get(layoutConfig, "aside.menu.dropdown") !== "true" &&
      objectPath.get(layoutConfig, "aside.self.fixed")
    ) {
      asideMenuRef.current.setAttribute("data-ktmenu-scroll", "1");
    }

    if (objectPath.get(layoutConfig, "aside.menu.dropdown") === "true") {
      asideMenuRef.current.setAttribute("data-ktmenu-dropdown", "1");
      asideMenuRef.current.setAttribute(
        "data-ktmenu-dropdown-timeout",
        objectPath.get(layoutConfig, "aside.menu.submenu.dropdown.hover-timeout")
      );
    }
  }, [layoutConfig]);

  const setupMenuOptions = useCallback(() => {
    const menuOptions = {
      // vertical scroll
      scroll: null, // submenu setup
      submenu: {
        desktop: {
          // by default the menu mode set to accordion in desktop mode
          default: "dropdown",
        },
        tablet: "accordion", // menu set to accordion in tablet mode
        mobile: "accordion", // menu set to accordion in mobile mode
      }, // accordion setup
      accordion: {
        expandAll: false, // allow having multiple expanded accordions in the menu
      },
    };

    // init aside menu
    let menuDesktopMode = "accordion";
    const dataKtmenuDropdown = asideMenuRef.current.getAttribute("data-ktmenu-dropdown");
    if (dataKtmenuDropdown === "1") {
      menuDesktopMode = "dropdown";
    }

    if (typeof objectPath.get(menuOptions, "submenu.desktop") === "object") {
      menuOptions.submenu.desktop.default = menuDesktopMode;
    }

    return menuOptions;
  }, []);

  /**
   * Use for fixed left aside menu, to show menu on mouseenter event.
   * @param e Event
   */
  const mouseEnter = useCallback(() => {
    // check if the left aside menu is fixed
    if (document.body.classList.contains("kt-aside--fixed")) {
      if (outsideTm.current) {
        clearTimeout(outsideTm.current);
        outsideTm.current = null;
      }

      insideTm.current = setTimeout(() => {
        // if the left aside menu is minimized
        if (
          document.body.classList.contains("kt-aside--minimize") &&
          // eslint-disable-next-line no-undef
          KTUtil.isInResponsiveRange("desktop")
        ) {
          removeCSSClass(document.body, "kt-aside--minimize");
          document.body.classList.add("kt-aside--minimize-hover");
        }
      }, 50);
    }
  }, []);

  /**
   * Use for fixed left aside menu, to show menu on mouseenter event.
   * @param e Event
   */
  const mouseLeave = () => {
    if (document.body.classList.contains("kt-aside--fixed")) {
      if (insideTm.current) {
        clearTimeout(insideTm.current);
        insideTm.current = null;
      }

      outsideTm.current = setTimeout(() => {
        // if the left aside menu is expand
        /* eslint-disable-next-line  */
        const kUtilIsResponsiveRange = KTUtil.isInResponsiveRange("desktop");
        const hasMinimizeHover = document.body.classList.contains("kt-aside--minimize-hover");
        if (
          hasMinimizeHover &&
          // eslint-disable-next-line no-undef
          kUtilIsResponsiveRange
        ) {
          // hide back the left aside menu
          removeCSSClass(document.body, "kt-aside--minimize-hover");
          document.body.classList.add("kt-aside--minimize");
        }
      }, 100);
    }
  };

  const onMenuItemSelected = useCallback(
    (item) => {
      if (item.page === "requests") {
        dispatch(setPage(0));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    initMenu(); // By ID

    const options = setupMenuOptions();
    const ktMenu = new KTMenu(asideMenuRef.current, options); // eslint-disable-line
  }, [initMenu, setupMenuOptions]);

  return (
    <Fragment>
      <div
        id="kt_aside_menu"
        ref={asideMenuRef}
        style={{ maxHeight: "90vh", position: "relative" }}
        onMouseEnter={mouseEnter}
        onMouseLeave={mouseLeave}
        data-ktmenu-vertical="1"
        className={clsx("kt-aside-menu", asideClassesFromConfig)}
        {...asideMenuAttr}
      >
        {disableAsideSelfDisplay && (
          <div className="kt-header-logo">
            <Link to="">
              <img alt="logo" src={headerLogo} />
            </Link>
          </div>
        )}
        <ul className={clsx("kt-menu__nav", ulClasses)}>
          <MenuList
            currentUrl={location.pathname}
            menuConfig={menuConfig}
            onMenuItemSelected={onMenuItemSelected}
            layoutConfig={layoutConfig}
          />
        </ul>
      </div>
    </Fragment>
  );
};

export default Menu;
